(() => {
  'use strict';

  // Clarity custom events — additive telemetry only, never behaviour. Guarded + try/catch so
  // a missing/broken window.clarity can never break the offer box. See
  // .claude/plans/cro-loop.md "Slice 1 — Sereni offer box → Clarity events".
  function safeClarity() {
    try {
      if (typeof window.clarity === 'function') {
        window.clarity.apply(null, arguments);
      }
    } catch (e) {
      /* never throw */
    }
  }

  // Pure pricing/messaging state for the Sereni multi-set offer box.
  // qty = PDP stepper quantity (sets), inCart = Sereni sets already in the cart (any colour).
  // Tier prices/thresholds mirror the two live automatic discounts — see
  // snippets/sereni-quantity-offer.liquid header comment for the discount ids.
  function sereniOfferState(qty, inCart) {
    const regular = 199;
    const tier2Price = 179;
    const tier3Price = 169;
    const tier2Min = 3;
    const tier3Min = 4;
    const n = Math.max(1, (Number(qty) || 0)) + Math.max(0, (Number(inCart) || 0));

    let tier = 1;
    let perSet = regular;
    if (n >= tier3Min) {
      tier = 3;
      perSet = tier3Price;
    } else if (n >= tier2Min) {
      tier = 2;
      perSet = tier2Price;
    }

    const total = perSet * n;
    const saving = (regular - perSet) * n;

    let message;
    if (n < tier2Min) {
      const need = tier2Min - n;
      const needWord = need === 1 ? 'set' : 'sets';
      message = n === 2
        ? `Add ${need} more ${needWord} to pay $179 per set ($537 for 3).`
        : `Add ${need} more ${needWord} to pay $179 per set.`;
    } else {
      message = `You save $${saving} — ${n} sets for $${total} ($${perSet} per set).`;
    }

    return { tier, perSet, total, saving, message };
  }

  function inCartFromCartJson(cartJson, productId) {
    if (!cartJson || !Array.isArray(cartJson.items)) return 0;
    return cartJson.items.reduce((sum, item) => (item.product_id === productId ? sum + item.quantity : sum), 0);
  }

  function money(cents, currency) {
    const curr = currency || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: curr,
      currencyDisplay: 'code'
    }).format(cents / 100);
  }

  function renderOffer(box, state, inCart, qty) {
    box.querySelectorAll('[data-sereni-offer-tier]').forEach(tierEl => {
      const button = tierEl.querySelector('[data-sereni-offer-select]');
      if (!button) return;
      const target = Number(button.dataset.sereniOfferSelect);
      const selectedRow = qty >= 4 ? 4 : qty >= 3 ? 3 : 1;
      const active = target === selectedRow;
      tierEl.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      const option = sereniOfferState(target, inCart);
      const price = button.querySelector('strong');
      if (price) price.textContent = money(option.perSet * target * 100, 'USD');
      const benefit = button.querySelector('small');
      if (benefit) {
        const chairs = target * 2;
        const saving = (199 - option.perSet) * target;
        benefit.textContent = chairs + ' chairs' + (saving ? ' · Save $' + saving : '');
      }
    });
    const message = box.querySelector('[data-sereni-offer-message]');
    if (message) message.textContent = state.message;
    const note = box.querySelector('[data-sereni-offer-cart-note]');
    if (note) {
      if (inCart > 0) {
        note.textContent = `Includes ${inCart} set${inCart === 1 ? '' : 's'} already in your cart.`;
        note.hidden = false;
      } else {
        note.hidden = true;
        note.textContent = '';
      }
    }
  }

  function init(box) {
    if (box.dataset.sereniOfferReady) return;
    box.dataset.sereniOfferReady = 'true';
    const root = box.closest('.sereni-editorial');
    if (!root) return;
    const quantityInput = root.querySelector('.sereni-editorial__form [name="quantity"]');
    if (!quantityInput) return;
    const productId = Number(box.dataset.productId);
    const currency = root.dataset.currency || 'USD';
    let inCart = 0;
    let lastState = null;

    // Clarity event/state tracking — each fires at most once per page view.
    const tierEventFired = { 2: false, 3: false };
    let maxTierReported = null; // null | '3sets' | '4plus'
    const tierRank = { '3sets': 1, '4plus': 2 };
    let cartReported = false;

    function reportMaxTier(label) {
      if (!maxTierReported || tierRank[label] > tierRank[maxTierReported]) {
        maxTierReported = label;
        safeClarity('set', 'sereni_offer_max_tier', label);
      }
    }

    function reportTierEvent(tier) {
      if (tier === 2 && !tierEventFired[2]) {
        tierEventFired[2] = true;
        safeClarity('event', 'sereni_offer_tier_3sets');
        reportMaxTier('3sets');
      } else if (tier === 3 && !tierEventFired[3]) {
        tierEventFired[3] = true;
        safeClarity('event', 'sereni_offer_tier_4plus');
        reportMaxTier('4plus');
      }
    }

    function applyTotals() {
      if (!lastState || !quantityInput.validity.valid || Number(quantityInput.value) <= 0) {
        return;
      }

      const steppedQty = Number(quantityInput.value);
      const elements = root.querySelectorAll('[data-add-total], [data-sticky-total]');

      elements.forEach(el => {
        if (lastState.tier > 1) {
          const originalTotal = 199 * 100 * steppedQty;
          const discountedTotal = lastState.perSet * 100 * steppedQty;
          const was = money(originalTotal, currency);
          const now = money(discountedTotal, currency);
          // Compare text, not innerHTML: Intl output contains U+00A0, which innerHTML serialises
          // as &nbsp; and would never match (observer loop).
          if (el.textContent !== `${was} ${now}` || !el.querySelector('.sereni-quantity-offer__was')) {
            el.innerHTML = '';
            const s = document.createElement('s');
            s.className = 'sereni-quantity-offer__was';
            s.textContent = was;
            el.append(s, ` ${now}`);
          }
        }
      });
    }

    function refresh() {
      const qty = Math.max(1, Number(quantityInput.value) || 1);
      lastState = sereniOfferState(qty, inCart);
      renderOffer(box, lastState, inCart, qty);
      const stickyOffer = root.querySelector('[data-sticky-offer]');
      if (stickyOffer) {
        stickyOffer.textContent = lastState.tier > 1
          ? `${qty} set${qty === 1 ? '' : 's'} $${lastState.perSet * qty} · save $${(199 - lastState.perSet) * qty}`
          : '3 sets $537 · save $60';
      }
      applyTotals();
      reportTierEvent(lastState.tier);
    }

    function fetchCart() {
      if (!root.dataset.cartUrl) return;
      const url = `${root.dataset.cartUrl.replace(/\/$/, '')}.js`;
      fetch(url, { headers: { Accept: 'application/json' } })
        .then(response => (response.ok ? response.json() : null))
        .then(cartJson => {
          if (cartJson) inCart = inCartFromCartJson(cartJson, productId);
          if (!cartReported && inCart > 0) {
            cartReported = true;
            safeClarity('set', 'sereni_sets_in_cart', String(inCart));
          }
          refresh();
        })
        .catch(() => { /* fail silently — stepper-only state already rendered */ });
    }

    // Set up observers to reapply totals when sereni-editorial.js overwrites them
    root.querySelectorAll('[data-add-total], [data-sticky-total]').forEach(el => {
      const observer = new MutationObserver(() => {
        applyTotals();
      });
      observer.observe(el, { childList: true, characterData: true, subtree: true });
    });

    box.querySelectorAll('[data-sereni-offer-select]').forEach(button => {
      const target = Number(button.dataset.sereniOfferSelect);
      const max = Number(quantityInput.max || Infinity);
      if (target > max) { button.disabled = true; return; }
      button.addEventListener('click', () => {
        const min = Number(quantityInput.min || 1);
        const step = Number(quantityInput.step || 1);
        const selected = Math.max(min, Math.min(max, target));
        if ((selected - min) % step !== 0) return;
        quantityInput.value = String(selected);
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        quantityInput.focus({ preventScroll: true });
      });
    });

    quantityInput.addEventListener('input', refresh);
    quantityInput.addEventListener('change', refresh);
    document.addEventListener('sereni:cart-updated', fetchCart);

    const form = root.querySelector('.sereni-editorial__form');
    if (form) {
      form.addEventListener('submit', () => {
        try {
          const tier = lastState ? lastState.tier : 1;
          safeClarity('event', tier > 1 ? 'sereni_atc_with_offer' : 'sereni_atc_no_offer');
        } catch (e) {
          /* never throw */
        }
      }, { passive: true });
    }

    safeClarity('event', 'sereni_offer_seen');
    refresh();
    fetchCart();
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { sereniOfferState, inCartFromCartJson };
  }

  if (typeof document !== 'undefined') {
    document.querySelectorAll('[data-sereni-offer]').forEach(init);
    document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-sereni-offer]').forEach(init));
  }
})();
